import { useEffect, useState } from 'react';
import { useTournament } from '@shootout-comps/core';
import { useLang } from '@shootout-comps/i18n';
import type { Tournament, Match, Player, Round } from '@shootout-comps/types';
import { SnookerBallSpinner } from '../components/SnookerBall';

const PLACEHOLDER = 'data:image/svg+xml,%3Csvg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 64 64"%3E%3Ccircle cx="32" cy="32" r="32" fill="%23236038"/%3E%3Ccircle cx="32" cy="24" r="10" fill="%23c9a84c" opacity="0.7"/%3E%3Cellipse cx="32" cy="52" rx="16" ry="10" fill="%23c9a84c" opacity="0.7"/%3E%3C/svg%3E';

function Avatar({ player, size = 40 }: { player: Player; size?: number }) {
  return (
    <div style={{ width: size, height: size, borderRadius: '50%', overflow: 'hidden', flexShrink: 0, border: '2px solid rgba(201,168,76,0.3)', background: '#1a4a2e' }}>
      <img src={player.profilePictureUrl ? player.profilePictureUrl : PLACEHOLDER} alt={player.name} style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
    </div>
  );
}

interface PublicTournamentBracketPageProps {
  tournamentId: string;
  onBack: () => void;
  onSpectateMatch: (matchId: string) => void;
}

export default function PublicTournamentBracketPage({ tournamentId, onBack, onSpectateMatch }: PublicTournamentBracketPageProps) {
  const { fetchPublicTournament } = useTournament();
  const { t } = useLang();
  const [tournament, setTournament] = useState<Tournament | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    setLoading(true);
    fetchPublicTournament(tournamentId)
      .then(setTournament)
      .catch((e: Error) => setError(e.message))
      .finally(() => setLoading(false));
  }, [tournamentId, fetchPublicTournament]);

  if (loading) {
    return (
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', minHeight: 'calc(100vh - 64px)', color: 'var(--chalk-dim)' }}>
        <SnookerBallSpinner size={36} />
      </div>
    );
  }

  if (error || !tournament) {
    return (
      <div style={{ maxWidth: '640px', margin: '2rem auto', padding: '0 1rem' }}>
        <button onClick={onBack} style={backBtnStyle}>{t.backToPublicList}</button>
        <div style={{ marginTop: '1rem', background: 'rgba(192,57,43,0.15)', border: '1px solid var(--red)', borderRadius: '8px', padding: '1rem', color: '#e74c3c' }}>
          {error ?? t.tournamentNotFound}
        </div>
      </div>
    );
  }

  const champion = tournament.status === 'complete'
    ? tournament.rounds[tournament.rounds.length - 1]?.matches[0]?.winner
    : null;
  const realPlayers = tournament.players.filter((p) => !p.isBye);

  return (
    <div style={{ padding: 'var(--page-pad)' }}>
      <button onClick={onBack} style={backBtnStyle}>{t.backToPublicList}</button>

      <div style={{
        maxWidth: '900px', margin: '1.25rem auto 0',
        background: 'linear-gradient(135deg, rgba(255,255,255,0.05), rgba(255,255,255,0.02))',
        border: '1px solid rgba(255,255,255,0.1)', borderRadius: '14px',
        padding: '1.5rem', marginBottom: '1.5rem',
      }}>
        <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: '1rem', flexWrap: 'wrap' }}>
          <div>
            <h2 style={{ fontFamily: 'Bebas Neue', fontSize: '2.2rem', color: 'var(--gold)', lineHeight: 1 }}>{tournament.name}</h2>
            <p style={{ color: 'var(--chalk-dim)', fontSize: '0.82rem', marginTop: '0.3rem' }}>👥 {realPlayers.length} players</p>
          </div>
          {champion && (
            <div style={{ textAlign: 'center' }}>
              <div style={{ fontSize: '1.5rem' }}>🏆</div>
              <div style={{ fontSize: '0.65rem', color: 'var(--chalk-dim)', textTransform: 'uppercase', letterSpacing: '0.08em' }}>Champion</div>
              <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginTop: '0.35rem' }}>
                <Avatar player={champion as Player} size={32} />
                <span style={{ fontWeight: 700, color: 'var(--gold)', fontSize: '0.95rem' }}>{(champion as Player).name}</span>
              </div>
            </div>
          )}
        </div>
      </div>

      {tournament.rounds.length === 0 && (
        <div style={{ textAlign: 'center', color: 'var(--chalk-dim)', padding: '2.5rem' }}>{t.awaitingDraw}</div>
      )}

      {tournament.rounds.length > 0 && (
        <div style={{ maxWidth: '100%', overflowX: 'auto' }}>
          <div style={{ display: 'flex', gap: '1rem', padding: '0 var(--page-pad) 1rem', alignItems: 'flex-start', minWidth: 'max-content', margin: '0 auto' }}>
            {tournament.rounds.map((round) => (
              <PublicRoundColumn key={round.round} round={round} onSelectMatch={(m) => onSpectateMatch(m._id)} />
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

function PublicRoundColumn({ round, onSelectMatch }: { round: Round; onSelectMatch: (m: Match) => void }) {
  const done = round.matches.every((m) => m.status === 'complete');
  return (
    <div style={{ minWidth: '210px', maxWidth: '230px', flexShrink: 0 }}>
      <div style={{ textAlign: 'center', marginBottom: '0.85rem', paddingBottom: '0.6rem', borderBottom: `2px solid ${done ? 'var(--gold)' : 'rgba(255,255,255,0.1)'}` }}>
        <h3 style={{ fontFamily: 'Bebas Neue', fontSize: '1.3rem', color: done ? 'var(--gold)' : 'var(--chalk)', letterSpacing: '0.04em' }}>
          {round.name}
        </h3>
      </div>
      <div style={{ display: 'flex', flexDirection: 'column', gap: '0.6rem' }}>
        {round.matches.map((match) => (
          <PublicMatchCard key={match._id} match={match} onSelect={() => onSelectMatch(match)} />
        ))}
      </div>
    </div>
  );
}

function PublicMatchCard({ match, onSelect }: { match: Match; onSelect: () => void }) {
  const { player1, player2, winner, score1, score2, status } = match;
  const complete = status === 'complete';
  const active = status === 'active';
  const isBye = player2?.isBye;
  const clickable = status !== 'pending' && !isBye && !!player2;

  return (
    <div
      onClick={() => clickable && onSelect()}
      style={{
        background: active ? 'rgba(46,204,113,0.07)' : complete ? 'linear-gradient(135deg, rgba(201,168,76,0.07), rgba(255,255,255,0.02))' : 'rgba(255,255,255,0.03)',
        border: `1px solid ${active ? 'rgba(46,204,113,0.3)' : complete ? 'rgba(201,168,76,0.2)' : 'rgba(255,255,255,0.06)'}`,
        borderRadius: '8px', padding: '0.55rem', cursor: clickable ? 'pointer' : 'default', transition: 'all 0.15s',
      }}
      className={clickable ? 'public-match-card' : ''}
    >
      {active && (
        <div style={{ display: 'flex', alignItems: 'center', gap: '0.3rem', justifyContent: 'center', marginBottom: '0.3rem' }}>
          <span style={{ width: '6px', height: '6px', borderRadius: '3px', background: '#2ecc71', display: 'inline-block' }} />
          <span style={{ fontSize: '0.62rem', color: '#2ecc71', fontWeight: 700, letterSpacing: '0.08em' }}>LIVE</span>
        </div>
      )}
      <PublicPlayerRow player={player1} isWinner={winner?._id === player1._id} score={score1} complete={complete} />
      <div style={{ display: 'flex', alignItems: 'center', gap: '0.4rem', padding: '0.1rem 0.5rem' }}>
        <div style={{ flex: 1, height: '1px', background: 'rgba(255,255,255,0.06)' }} />
        <span style={{ fontSize: '0.58rem', color: 'var(--chalk-dim)', letterSpacing: '0.1em' }}>VS</span>
        <div style={{ flex: 1, height: '1px', background: 'rgba(255,255,255,0.06)' }} />
      </div>
      {player2
        ? <PublicPlayerRow player={player2} isWinner={winner?._id === player2._id} score={score2} complete={complete} />
        : <div style={{ fontSize: '0.72rem', color: 'var(--chalk-dim)', padding: '0.3rem 0.5rem', fontStyle: 'italic' }}>BYE</div>
      }
      {clickable && (
        <div style={{ textAlign: 'center', marginTop: '0.4rem' }}>
          <span style={{ fontSize: '0.62rem', color: active ? '#2ecc71' : 'var(--gold)', letterSpacing: '0.06em' }}>{active ? '👀 Watch live' : '→ View result'}</span>
        </div>
      )}
      <style>{`.public-match-card:hover{transform:translateY(-1px);box-shadow:0 4px 14px rgba(0,0,0,0.3)}`}</style>
    </div>
  );
}

function PublicPlayerRow({ player, isWinner, score, complete }: { player: Player; isWinner: boolean; score: number | null; complete: boolean }) {
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', padding: '0.35rem 0.4rem', background: isWinner ? 'rgba(201,168,76,0.1)' : 'transparent', borderRadius: '5px', opacity: complete && !isWinner ? 0.45 : 1 }}>
      <Avatar player={player} size={24} />
      <span style={{ flex: 1, fontSize: '0.82rem', fontWeight: isWinner ? 600 : 400, color: isWinner ? 'var(--gold)' : 'var(--chalk)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
        {player.name}
      </span>
      {score !== null && (
        <span style={{ fontFamily: 'Bebas Neue', fontSize: '1.05rem', color: isWinner ? 'var(--gold)' : 'var(--chalk-dim)', minWidth: '20px', textAlign: 'right' }}>
          {score}
        </span>
      )}
      {isWinner && <span style={{ fontSize: '0.6rem' }}>✓</span>}
    </div>
  );
}

const backBtnStyle: React.CSSProperties = {
  background: 'transparent', color: 'var(--chalk-dim)', border: '1px solid rgba(255,255,255,0.12)',
  borderRadius: '6px', padding: '0.4rem 0.9rem', fontSize: '0.78rem', cursor: 'pointer', minHeight: '36px',
};
